import { useEffect, useSyncExternalStore } from "react";
import { setDarkMode } from "../context";

export type ThemeMode = "light" | "dark" | "system";

export const THEME_MODE_KEY = "app-theme-mode";
const THEME_MODE_EVENT = "app-theme-mode-changed";
const DARK_QUERY = "(prefers-color-scheme: dark)";

/** Stored choice; defaults to light so existing users keep today's look until they pick otherwise. */
export function readThemeMode(): ThemeMode {
  try {
    const value = localStorage.getItem(THEME_MODE_KEY);
    return value === "dark" || value === "system" ? value : "light";
  } catch {
    return "light";
  }
}

export function writeThemeMode(mode: ThemeMode) {
  try {
    localStorage.setItem(THEME_MODE_KEY, mode);
  } catch {
    /* storage unavailable: the choice still applies for this page view */
  }
  window.dispatchEvent(new Event(THEME_MODE_EVENT));
}

export function resolveDarkMode(mode: ThemeMode, systemPrefersDark: boolean): boolean {
  return mode === "system" ? systemPrefersDark : mode === "dark";
}

function subscribe(onChange: () => void) {
  window.addEventListener(THEME_MODE_EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(THEME_MODE_EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

/** Current theme preference (light / dark / system), shared by every component that reads it. */
export function useThemeMode(): ThemeMode {
  return useSyncExternalStore(subscribe, readThemeMode, () => "light");
}

/** Mount once near the app root: keeps the MUI controller's darkMode in sync with the preference. */
export function useApplyThemeMode(dispatch: any) {
  const mode = useThemeMode();
  useEffect(() => {
    const media = typeof window.matchMedia === "function" ? window.matchMedia(DARK_QUERY) : null;
    const apply = () => setDarkMode(dispatch, resolveDarkMode(mode, Boolean(media?.matches)));
    apply();
    if (mode !== "system" || !media) return undefined;
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [mode, dispatch]);
}
